import { useState } from "react";

import { Tab } from "@headlessui/react";
import MarkdownRender from "../../components/common/MarkdownRender";
import { TabNoData } from "../../components/common/NoData";
import { HeroWithTextCenter } from "../../components/common/slider/HeroSlider";

import TextNavBackground from "../../components/common/TextNavBackground";
import PeopleProfileCard from "../../components/elements/people/PeopleProfileCard";

import { apiWrapper } from "../../utils/api/apiWrapper";
import {
  kLifePatronURL,
  kBoardMembersURL,
  kManagementMembersURL,
  kTechnicalTeamMembersURL,
} from "../../utils/constants/apiUrls";

import { GUEST_TOKEN } from "../../utils/constants/names";
import { ONE_MONTH_REFRESH_INTERVAL_SECONDS } from "../../utils/constants/numbers";
import {
  BOARD_DETAIL_URL,
  MANAGEMENT_DETAIL_URL,
  PEOPLE_LIST_URL,
  TECHNICAL_DETAIL_URL,
} from "../../utils/constants/websiteUrls";
import { customStaticSeoConfig } from "../../utils/helpers/helpers";
import Link from "next/link";
import { club1 } from "../../utils/constants/images";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const PeopleList = ({
  lifePatronProfile,
  boardMemberList,
  managementMemberList,
  technicalTeamList,
}) => {
  let [technicalTeamMembers] = useState({
    Men: technicalTeamList?.men,
    Women: technicalTeamList?.women,
    Academy: [...technicalTeamList?.under_20, ...technicalTeamList?.under_17],
  });

  const { life_patron: lifePatron } = lifePatronProfile;

  const { life_patron_images: lifePatronImages } = lifePatronProfile;

  const lifePatronGallery = lifePatronImages.map(function (item, index) {
    return item.image;
  });

  const lifePatronFullName = `${lifePatron.first_name} ${lifePatron.last_name}`;
  const lifePatronBiography = lifePatron.biography;

  return (
    <>
      {customStaticSeoConfig("Asante Kotoko People")}

      <TextNavBackground title={"People"} />

      <div className="block w-full text-base sm:text-sm rounded-md">
        <Tab.Group>
          <Tab.List className="-mb-px flex justify-center items-center space-x-8 overflow-x-auto bg-gray-200 pl-14">
            <Tab
              className={({ selected }) =>
                classNames(
                  "whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none",
                  selected
                    ? "border-red-500 dark:border-green-500 text-red-600 dark:text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 active:border-none"
                )
              }
            >
              Life Patron
            </Tab>
            <Tab
              className={({ selected }) =>
                classNames(
                  "whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none",
                  selected
                    ? "border-red-500 dark:border-green-500 text-red-600 dark:text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 active:border-none"
                )
              }
            >
              Board
            </Tab>
            <Tab
              className={({ selected }) =>
                classNames(
                  "whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none",
                  selected
                    ? "border-red-500 dark:border-green-500 text-red-600 dark:text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 active:border-none"
                )
              }
            >
              Management
            </Tab>
            <Tab
              className={({ selected }) =>
                classNames(
                  "whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none",
                  selected
                    ? "border-red-500 dark:border-green-500 text-red-600 dark:text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 active:border-none"
                )
              }
            >
              Technical
            </Tab>
          </Tab.List>
          <Tab.Panels>
            {/** LIFE PATRON TAB PANEL */}
            <Tab.Panel className="mt-4">
              {lifePatronBiography === "" && (
                <>
                  <TabNoData />
                </>
              )}

              {lifePatronBiography !== "" && (
                <>
                  <HeroWithTextCenter
                    title={lifePatronFullName}
                    gallery={club1}
                  />

                  <MarkdownRender
                    title={lifePatronFullName}
                    caption="Profile of the life patron and owner of Asante Kotoko SC."
                    url={PEOPLE_LIST_URL}
                    markdownData={lifePatronBiography}
                  />
                </>
              )}
            </Tab.Panel>

            {/** BOARD MEMBERS TAB PANEL */}
            <Tab.Panel className="mt-4 sm:mb-10 max-w-2xl mx-auto lg:max-w-7xl lg:px-8 px-6">
              {boardMemberList.length === 0 && (
                <>
                  <TabNoData />
                </>
              )}

              {boardMemberList.length !== 0 && (
                <>
                  <div className="space-y-12">
                    <ul
                      role="list"
                      className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 lg:grid-cols-3 lg:gap-8"
                    >
                      {boardMemberList.map((member) => (
                        <li
                          key={member.id}
                          className="py-10 px-6 bg-gray-800 text-center rounded-md xl:px-10 xl:text-left transform transition duration-500 hover:scale-105"
                        >
                          <Link
                            href={`${PEOPLE_LIST_URL}/${BOARD_DETAIL_URL}/${member.slug}`}
                          >
                            <PeopleProfileCard member={member} />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </Tab.Panel>

            {/** MANAGEMENT MEMBERS TAB PANEL */}
            <Tab.Panel className="mt-4 max-w-2xl mx-auto lg:max-w-7xl lg:px-8 px-6">
              {managementMemberList.length === 0 && (
                <>
                  <TabNoData />
                </>
              )}

              {managementMemberList.length !== 0 && (
                <>
                  <div className="space-y-12">
                    <ul
                      role="list"
                      className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 lg:grid-cols-3 lg:gap-8"
                    >
                      {managementMemberList.map((member) => (
                        <li
                          key={member.id}
                          className="py-10 px-6 bg-gray-800 text-center rounded-md xl:px-10 xl:text-left transform transition duration-500 hover:scale-105"
                        >
                          <Link
                            href={`${PEOPLE_LIST_URL}/${MANAGEMENT_DETAIL_URL}/${member.slug}`}
                          >
                            <PeopleProfileCard member={member} />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </Tab.Panel>

            {/** TECHNICAL TEAM MEMBERS TAB PANEL */}
            <Tab.Panel className="mt-4 mb-10 max-w-2xl mx-auto lg:max-w-7xl lg:px-8 px-6">
              {technicalTeamMembers.Men.length === 0 && (
                <>
                  <TabNoData />
                </>
              )}

              {technicalTeamMembers.Men.length !== 0 && (
                <>
                  {/** MEN TECHNICAL TEAM MEMBERS TAB PANEL */}
                  {technicalTeamMembers.Men.length !== 0 && (
                    <div>
                      <h2 className="text-lg font-extrabold tracking-tight text-gray-900 border-b-2">
                        Men
                      </h2>

                      <div className="space-y-12">
                        <ul
                          role="list"
                          className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 lg:grid-cols-3 lg:gap-8"
                        >
                          {technicalTeamMembers.Men.map((member) => (
                            <li
                              key={member.id}
                              className="py-10 px-6 bg-gray-800 text-center rounded-md xl:px-10 xl:text-left transform transition duration-500 hover:scale-105"
                            >
                              <Link
                                href={`${PEOPLE_LIST_URL}/${TECHNICAL_DETAIL_URL}/${member.slug}`}
                              >
                                <PeopleProfileCard member={member} />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/** WOMEN TECHNICAL TEAM MEMBERS TAB PANEL */}
                  {technicalTeamMembers.Women.length !== 0 && (
                    <div>
                      <h2 className="text-lg font-extrabold tracking-tight text-gray-900 border-b-2 mt-16">
                        Women
                      </h2>

                      <div className="space-y-12">
                        <ul
                          role="list"
                          className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 lg:grid-cols-3 lg:gap-8"
                        >
                          {technicalTeamMembers.Women.map((member) => (
                            <li
                              key={member.id}
                              className="py-10 px-6 bg-gray-800 text-center rounded-md xl:px-10 xl:text-left transform transition duration-500 hover:scale-105"
                            >
                              <Link
                                href={`${PEOPLE_LIST_URL}/${TECHNICAL_DETAIL_URL}/${member.slug}`}
                              >
                                <PeopleProfileCard member={member} />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/** ACADEMY TECHNICAL TEAM MEMBERS TAB PANEL */}
                  {technicalTeamMembers.Academy.length !== 0 && (
                    <div>
                      <h2 className="text-lg font-extrabold tracking-tight text-gray-900 border-b-2 mt-16">
                        Academy
                      </h2>

                      <div className="space-y-12">
                        <ul
                          role="list"
                          className="space-y-4 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 lg:grid-cols-3 lg:gap-8"
                        >
                          {technicalTeamMembers.Academy.map((member) => (
                            <li
                              key={member.id}
                              className="py-10 px-6 bg-gray-800 text-center rounded-md xl:px-10 xl:text-left transform transition duration-500 hover:scale-105"
                            >
                              <Link
                                href={`${PEOPLE_LIST_URL}/${TECHNICAL_DETAIL_URL}/${member.slug}`}
                              >
                                <PeopleProfileCard member={member} />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </>
  );
};

export default PeopleList;

export const getStaticProps = async (context) => {
  const token = process.env.GUSET_USER_TOKEN
    ? process.env.GUSET_USER_TOKEN
    : GUEST_TOKEN;

  const lifePatronProfile = await apiWrapper.get(kLifePatronURL, token);
  const boardMemberList = await apiWrapper.get(kBoardMembersURL, token);
  const managementMemberList = await apiWrapper.get(
    kManagementMembersURL,
    token
  );
  const technicalTeamList = await apiWrapper.get(
    kTechnicalTeamMembersURL,
    token
  );

  return {
    props: {
      lifePatronProfile: lifePatronProfile?.data,
      boardMemberList: boardMemberList?.data,
      managementMemberList: managementMemberList?.data,
      technicalTeamList: technicalTeamList?.data,
    },
    revalidate: ONE_MONTH_REFRESH_INTERVAL_SECONDS,
  };
};
